import { color as d3Color } from 'd3';

import styles from './chord-chart.module.css';

type Point = { x: number; y: number };

const polar = (angle: number, radius: number): Point => ({
  x: radius * Math.sin(angle),
  y: -radius * Math.cos(angle),
});

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/* ------------------------------- Triangle -------------------------------- */
/**
 * Small persistent filled triangle anchored at the inner edge of the target
 * arc, pointing radially outward toward the chart center. Tip sits at the
 * arc; base extends inward.
 */

const TRIANGLE_TIP_INSET = 8;
const TRIANGLE_HALF_WIDTH_RATIO = 0.18;
const TRIANGLE_HALF_MIN = 3.5;
const TRIANGLE_HALF_MAX = 9;
const TRIANGLE_LENGTH_RATIO = 1.4;
const TRIANGLE_MIN_ARC_LENGTH_RATIO = 2.4;
const TRIANGLE_OPACITY_BASE = 0.4;
const TRIANGLE_OPACITY_HIGHLIGHT = 0.9;
const TRIANGLE_DARKEN = 0.5;

type DirectionTriangleProps = {
  targetMidAngle: number;
  targetArcLength: number;
  innerRadius: number;
  color: string;
  isHighlighted: boolean;
};

export const ChordDirectionTriangle = ({
  targetMidAngle,
  targetArcLength,
  innerRadius,
  color,
  isHighlighted,
}: DirectionTriangleProps) => {
  const halfWidth = clamp(
    targetArcLength * TRIANGLE_HALF_WIDTH_RATIO,
    TRIANGLE_HALF_MIN,
    TRIANGLE_HALF_MAX
  );
  if (targetArcLength < halfWidth * TRIANGLE_MIN_ARC_LENGTH_RATIO) return null;

  const tipRadius = innerRadius - TRIANGLE_TIP_INSET;
  if (tipRadius <= 0) return null;

  const length = halfWidth * TRIANGLE_LENGTH_RATIO;
  const tip = polar(targetMidAngle, tipRadius);
  // SVG rotate(α) maps local (0, 1) → (-sin α, cos α). For α = targetMidAngle
  // (in degrees) this aligns local +y with the inward radial direction, so the
  // body extends from the tip toward the chart center.
  const angleDeg = (targetMidAngle * 180) / Math.PI;
  const d = `M 0 0 L ${-halfWidth} ${length} L ${halfWidth} ${length} Z`;
  const fill = d3Color(color)?.darker(TRIANGLE_DARKEN).formatHex() ?? color;
  const opacity = isHighlighted
    ? TRIANGLE_OPACITY_HIGHLIGHT
    : TRIANGLE_OPACITY_BASE;

  return (
    <path
      d={d}
      transform={`translate(${tip.x} ${tip.y}) rotate(${angleDeg})`}
      fill={fill}
      fillOpacity={opacity}
      className={styles.directionShape}
      data-testid="chord-chart__direction-triangle"
    />
  );
};
